
import { useState, useRef } from 'react'
import useSWR from 'swr'
import {
  Database, Play, RefreshCw, Table2, Hash, Clock,
  CheckCircle2, AlertCircle, ChevronDown, ChevronRight,
  TerminalSquare, LayoutGrid, Info,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { D1_WORKER_URL } from '@/lib/constants'

const fetcher = (url: string) => fetch(url).then(r => r.json())

interface TableInfo {
  name: string
  row_count: number
  sql: string
}

interface DBInfo {
  tables: TableInfo[]
  latency_ms: number
  healthy: boolean
}

interface QueryResult {
  results: Record<string, unknown>[]
  success: boolean
  meta?: { duration: number; rows_read: number; rows_written: number; changes: number }
  duration_ms?: number
  error?: string
}

const QUICK_QUERIES = [
  { label: 'All Tables', sql: "SELECT name, sql FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%'" },
  { label: 'Recent Usage', sql: 'SELECT * FROM usage_logs ORDER BY created_at DESC LIMIT 25' },
  { label: 'Active Keys', sql: "SELECT id, name, fingerprint, permissions, request_count FROM api_keys WHERE is_active = 1 ORDER BY request_count DESC" },
  { label: 'Providers', sql: 'SELECT id, name, provider_type, endpoint, is_active, priority FROM providers ORDER BY priority ASC' },
  { label: 'Users', sql: 'SELECT id, email, role, created_at, last_login_at FROM users ORDER BY created_at DESC' },
]

function TableSchema({ table }: { table: TableInfo }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2.5 w-full px-3 py-2.5 bg-muted/30 hover:bg-muted/60 transition-colors text-left"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
        <Table2 className="w-3.5 h-3.5 text-lava flex-shrink-0" />
        <span className="text-xs font-mono font-semibold text-foreground">{table.name}</span>
        <Badge variant="outline" className="ml-auto text-[9px] h-4 px-1.5 border-border text-muted-foreground font-mono">
          {table.row_count.toLocaleString()} rows
        </Badge>
      </button>
      {open && (
        <div className="p-3 bg-card border-t border-border">
          <pre className="text-[10px] font-mono text-muted-foreground whitespace-pre-wrap leading-relaxed overflow-x-auto">
            {table.sql}
          </pre>
        </div>
      )}
    </div>
  )
}

function ResultTable({ results }: { results: Record<string, unknown>[] }) {
  if (!results.length) return (
    <div className="flex items-center justify-center h-20 text-xs text-muted-foreground">
      Query executed — 0 rows returned
    </div>
  )

  const columns = Object.keys(results[0])

  return (
    <div className="overflow-auto max-h-72">
      <table className="w-full text-[11px] font-mono border-collapse">
        <thead className="sticky top-0">
          <tr className="bg-muted border-b border-border">
            {columns.map(col => (
              <th key={col} className="text-left px-3 py-1.5 text-muted-foreground font-semibold whitespace-nowrap border-r border-border/50 last:border-r-0">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {results.map((row, i) => (
            <tr key={i} className="border-b border-border/30 hover:bg-muted/30 transition-colors">
              {columns.map(col => (
                <td key={col} className="px-3 py-1.5 text-foreground whitespace-nowrap border-r border-border/30 last:border-r-0 max-w-[240px] truncate" title={String(row[col] ?? '')}>
                  {row[col] === null ? (
                    <span className="text-muted-foreground/50 italic">NULL</span>
                  ) : (
                    String(row[col])
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function DatabasePage() {
  const { data: dbInfo, isLoading: infoLoading, mutate: refreshInfo } = useSWR<DBInfo>('/api/database/info', fetcher)
  const [sql, setSql] = useState('SELECT * FROM users LIMIT 10')
  const [result, setResult] = useState<QueryResult | null>(null)
  const [running, setRunning] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  async function runQuery() {
    if (!sql.trim()) return
    setRunning(true)
    setResult(null)
    try {
      const res = await fetch('/api/database/query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql: sql.trim() }),
      })
      const data = await res.json()
      setResult(data)
    } catch (err) {
      setResult({ results: [], success: false, error: String(err) })
    } finally {
      setRunning(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      runQuery()
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md bg-lava/10">
            <Database className="w-4 h-4 text-lava" />
          </div>
          <div>
            <h2 className="text-base font-bold text-foreground">EdgeSQL D1</h2>
            <p className="text-xs text-muted-foreground font-mono truncate">{D1_WORKER_URL}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dbInfo && (
            <Badge
              variant="outline"
              className={`text-[10px] h-6 px-2 gap-1.5 ${dbInfo.healthy ? 'border-success/30 text-success bg-success/5' : 'border-destructive/30 text-destructive bg-destructive/5'}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${dbInfo.healthy ? 'bg-success animate-pulse' : 'bg-destructive'}`} />
              {dbInfo.healthy ? `${dbInfo.latency_ms}ms` : 'Unreachable'}
            </Badge>
          )}
          <Button variant="outline" size="sm" className="h-8 text-xs border-border gap-1.5" onClick={() => refreshInfo()}>
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
        </div>
      </div>

      <Tabs defaultValue="query">
        <TabsList className="bg-muted border border-border h-8 p-0.5">
          <TabsTrigger value="query" className="text-xs h-7 px-3 gap-1.5 data-[state=active]:bg-card">
            <TerminalSquare className="w-3 h-3" /> SQL Console
          </TabsTrigger>
          <TabsTrigger value="schema" className="text-xs h-7 px-3 gap-1.5 data-[state=active]:bg-card">
            <LayoutGrid className="w-3 h-3" /> Schema Browser
          </TabsTrigger>
        </TabsList>

        {/* SQL Console */}
        <TabsContent value="query" className="mt-4 space-y-3">
          {/* Quick queries */}
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[10px] text-muted-foreground self-center font-medium">Quick:</span>
            {QUICK_QUERIES.map(q => (
              <button
                key={q.label}
                onClick={() => setSql(q.sql)}
                className="text-[10px] font-mono px-2 py-1 rounded bg-muted border border-border hover:border-lava/40 hover:text-lava transition-colors text-muted-foreground"
              >
                {q.label}
              </button>
            ))}
          </div>

          {/* Editor */}
          <div className="rounded-md border border-border overflow-hidden bg-card">
            <div className="flex items-center justify-between px-3 py-2 bg-muted/60 border-b border-border">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-warning/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-success/60" />
              </div>
              <span className="text-[10px] text-muted-foreground font-mono">SQLite · D1 REST API</span>
              <Button
                size="sm"
                onClick={runQuery}
                disabled={running}
                className="h-6 text-[10px] lava-gradient text-lava-foreground border-0 px-3 gap-1 hover:opacity-90"
              >
                {running ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                {running ? 'Running...' : 'Run  ⌘↵'}
              </Button>
            </div>
            <Textarea
              ref={textareaRef}
              value={sql}
              onChange={e => setSql(e.target.value)}
              onKeyDown={handleKeyDown}
              className="font-mono text-xs border-0 rounded-none bg-card resize-none min-h-[120px] focus-visible:ring-0 focus-visible:ring-offset-0 leading-relaxed text-foreground p-3"
              spellCheck={false}
              placeholder="SELECT * FROM users LIMIT 10"
            />
          </div>

          {/* Result */}
          {result && (
            <Card className={`bg-card border ${result.success === false || result.error ? 'border-destructive/30' : 'border-border'}`}>
              <CardHeader className="pb-2 pt-3 px-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {result.error ? (
                      <AlertCircle className="w-3.5 h-3.5 text-destructive" />
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                    )}
                    <span className="text-xs font-semibold text-foreground">
                      {result.error ? 'Query Failed' : `${result.results.length} row${result.results.length !== 1 ? 's' : ''} returned`}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-muted-foreground font-mono">
                    {result.duration_ms !== undefined && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {result.duration_ms}ms
                      </span>
                    )}
                    {result.meta && (
                      <>
                        <span>read: {result.meta.rows_read}</span>
                        <span>written: {result.meta.rows_written}</span>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 pb-2">
                {result.error ? (
                  <div className="mx-3 p-2 rounded bg-destructive/5 border border-destructive/20 text-xs font-mono text-destructive">
                    {result.error}
                  </div>
                ) : (
                  <ResultTable results={result.results} />
                )}
              </CardContent>
            </Card>
          )}

          {/* Info notice */}
          <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              DROP and TRUNCATE are blocked via UI. All queries execute directly against the D1 REST Worker.
              Use <code className="font-mono text-lava">⌘+Enter</code> to run.
            </span>
          </div>
        </TabsContent>

        {/* Schema Browser */}
        <TabsContent value="schema" className="mt-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <Card className="bg-card border-border">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="p-2 rounded bg-lava/10">
                  <Table2 className="w-3.5 h-3.5 text-lava" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">Tables</p>
                  <p className="text-lg font-bold text-foreground tabular-nums">{dbInfo?.tables?.length ?? '—'}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="p-2 rounded bg-cyan-accent/10">
                  <Hash className="w-3.5 h-3.5 text-cyan-accent" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">Total Rows</p>
                  <p className="text-lg font-bold text-foreground tabular-nums">
                    {dbInfo?.tables?.reduce((s, t) => s + t.row_count, 0).toLocaleString() ?? '—'}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="p-2 rounded bg-success/10">
                  <Clock className="w-3.5 h-3.5 text-success" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">Latency</p>
                  <p className="text-lg font-bold text-foreground tabular-nums">
                    {dbInfo ? `${dbInfo.latency_ms}ms` : '—'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {infoLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-10 bg-muted rounded-md animate-pulse" />
              ))}
            </div>
          ) : !dbInfo?.tables?.length ? (
            <Card className="border-dashed border-border bg-card">
              <CardContent className="py-10 text-center">
                <Database className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-sm text-foreground font-medium">No tables found</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Run the schema migration to initialize the database
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {dbInfo.tables.map(t => (
                <TableSchema key={t.name} table={t} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
